import React, { useLayoutEffect, useRef, useState } from 'react';
import { api, mensajeError } from '../../../lib/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Bot, Camera, Loader2, Mic, RefreshCw, RotateCcw, Send } from 'lucide-react';
import { COLOR_AJENA, COLOR_MIA } from '../../chat/BurbujaChat';
import { BOTON_VERDE, EstadoRespuesta, FOCO, claveUsable, comoObjeto } from './comun';
import { PROCEDENCIAS } from './PlantillasBot';

/**
 * «Probar el bot»: un simulador que NO envía nada ni escribe en ningún lead.
 * Se rellenan los datos de un lead inventado, se escribe como si fuera el
 * contacto y se ve lo que contestaría el bot con su estado debajo. Cada
 * respuesta es una llamada de verdad a Claude (POST /api/whatsapp/bot/probar)
 * con TODO el historial; se puede probar con el texto del editor sin
 * guardarlo.
 */

export const CAMPOS_LEAD = [
  ['nombre', 'Nombre'],
  ['procedencia', 'Procedencia'],
  ['tratamiento', 'Tratamiento de interés'],
  ['que_mejorar', 'Qué quiere mejorar'],
  ['presupuesto', 'Presupuesto indicado'],
  ['plazo', 'Plazo indicado'],
  ['ciudad', 'Ciudad'],
  ['modalidad', 'Modalidad de valoración'],
];

export const LEAD_VACIO = {
  nombre: '', procedencia: '', tratamiento: '', que_mejorar: '', presupuesto: '', plazo: '',
  ciudad: '', modalidad: '', mensaje: '', apertura_enviada: false,
};

function normal(s) {
  return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

function poner(texto, n, valor) {
  // Con función: un «$» en el valor no se lee como patrón de reemplazo.
  return valor ? texto.replace(new RegExp(`\\{\\{\\s*${n}\\s*\\}\\}`, 'g'), () => valor) : texto;
}

/**
 * La apertura guardada en Plantillas, como le llegaría al lead, para empezar
 * la conversación con ella. Con nombre, `apertura`: {{1}} nombre, {{2}} tema,
 * {{3}} vía. Sin nombre, `apertura_sin_nombre`: {{1}} tema, {{2}} vía. El
 * tema sale de la tabla de temas (o el tema por defecto) y la vía, de los
 * textos de procedencias. Lo que no se sepa se deja a la vista.
 */
export function aperturaDePlantillas(plantillas, lead, via = '') {
  const d = comoObjeto(plantillas);
  const lista = Array.isArray(d.plantillas) ? d.plantillas : [];
  const nombre = (lead.nombre || '').trim();
  const conNombre = lista.find((p) => p.clave === 'apertura');
  const sinNombre = lista.find((p) => p.clave === 'apertura_sin_nombre');
  const p = (!nombre && sinNombre?.texto) ? sinNombre : conNombre;
  if (!p || !p.texto) return { texto: '', botones: [] };
  const temas = comoObjeto(d.temas);
  const clave = Object.keys(temas).find((k) => normal(k) === normal(lead.tratamiento));
  const tema = (lead.tratamiento && clave ? temas[clave] : '') || d.tema_por_defecto || '';
  const textoVia = via ? (comoObjeto(d.procedencias)[via] || '') : '';
  let texto = p.texto;
  if (p.clave === 'apertura') {
    texto = poner(poner(poner(texto, 1, nombre), 2, tema), 3, textoVia);
  } else {
    texto = poner(poner(texto, 1, tema), 2, textoVia);
  }
  const botones = (Array.isArray(p.botones) ? p.botones : []).filter((b) => b && String(b).trim());
  return { texto, botones };
}

/** Lo que se manda como historial: la apertura (si la hay) y cada turno, en orden. */
export function historialDe(lead, apertura, turnos) {
  const h = [];
  if (lead.apertura_enviada && apertura.trim()) h.push({ autor: 'bot', texto: apertura.trim() });
  turnos.forEach((t) => {
    if (t.autor === 'contacto') h.push({ autor: 'contacto', texto: t.texto });
    else (t.mensajes || []).forEach((m) => h.push({ autor: 'bot', texto: m }));
  });
  return h;
}

function Burbuja({ mia, children, testid, etiqueta }) {
  return (
    <div className={`flex ${mia ? 'justify-end' : 'justify-start'} mt-1.5`} data-testid={testid}>
      <div className={`rounded-lg shadow-[0_1px_0.5px_rgba(11,20,26,0.13)] text-[14px] leading-[1.35] px-2.5 py-1.5
        max-w-[88%] sm:max-w-[75%] whitespace-pre-wrap break-words ${mia ? COLOR_MIA : COLOR_AJENA}`}>
        {etiqueta ? <div className="text-[11.5px] font-medium text-[#54656F] mb-0.5 flex items-center gap-1">{etiqueta}</div> : null}
        {children}
      </div>
    </div>
  );
}

let siguienteId = 0;
const nuevoId = () => { siguienteId += 1; return `t${siguienteId}`; };

export default function SimuladorBot({ bot, textoEditor, plantillas }) {
  const [lead, setLead] = useState(LEAD_VACIO);
  // La apertura sale sola de Plantillas (con el nombre, el tema y la vía);
  // si se cambia a mano, manda lo escrito hasta «Volver a poner…».
  const [aperturaManual, setAperturaManual] = useState(null);
  const [via, setVia] = useState('');
  const [turnos, setTurnos] = useState([]);
  const [entrada, setEntrada] = useState('');
  const [pensando, setPensando] = useState(false);
  const [error, setError] = useState('');
  const [usarEditor, setUsarEditor] = useState(false);
  const rondaRef = useRef(0);
  const chatRef = useRef(null);

  const textoVigente = bot.prompt?.texto || '';
  const hayCambiosEditor = !!(textoEditor || '').trim() && textoEditor !== textoVigente;
  const conEditor = usarEditor && hayCambiosEditor;
  const automatica = aperturaDePlantillas(plantillas, lead, via);
  const apertura = aperturaManual ?? automatica.texto;
  const { botones } = automatica;
  const empezada = turnos.length > 0;
  const procedencias = comoObjeto(comoObjeto(plantillas).procedencias);

  useLayoutEffect(() => {
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turnos, pensando, error]);

  const ponerLead = (campo, valor) => setLead((l) => ({ ...l, [campo]: valor }));

  const alternarApertura = (activa) => setLead((l) => ({ ...l, apertura_enviada: activa }));

  const pedir = async (lista) => {
    const ronda = rondaRef.current;
    setPensando(true);
    setError('');
    const cuerpo = { lead: { ...lead }, mensajes: historialDe(lead, apertura, lista) };
    if (conEditor) cuerpo.texto_prompt = textoEditor;
    try {
      const r = await api.post('/api/whatsapp/bot/probar', cuerpo);
      if (rondaRef.current !== ronda) return;
      const d = comoObjeto(r.data);
      const paraEnviar = Array.isArray(d.mensajes_para_enviar) ? d.mensajes_para_enviar : null;
      const mensajes = paraEnviar && paraEnviar.length ? paraEnviar : (Array.isArray(d.mensajes) ? d.mensajes : []);
      setTurnos((ts) => [...ts, {
        id: nuevoId(), autor: 'bot', mensajes, estado: d.estado, ms: d.ms, uso: d.uso, modelo: d.modelo,
        conEditor: !!cuerpo.texto_prompt,
      }]);
    } catch (err) {
      if (rondaRef.current !== ronda) return;
      setError(mensajeError(err, 'El bot no ha podido contestar'));
    } finally {
      if (rondaRef.current === ronda) setPensando(false);
    }
  };

  const enviar = (texto) => {
    const t = String(texto ?? entrada).trim();
    if (!t || pensando) return;
    const lista = [...turnos, { id: nuevoId(), autor: 'contacto', texto: t }];
    setTurnos(lista);
    if (texto === undefined) setEntrada('');
    pedir(lista);
  };

  const reintentar = () => { if (!pensando) pedir(turnos); };

  const empezarDeNuevo = () => {
    rondaRef.current += 1;
    setTurnos([]);
    setEntrada('');
    setError('');
    setPensando(false);
  };

  const sinClave = !claveUsable(bot);
  const sinInstrucciones = !bot.prompt && !conEditor;

  return (
    <div className="space-y-3">
      <details open className="rounded-lg border p-3" data-testid="bot-sim-lead">
        <summary className={`cursor-pointer select-none text-sm font-medium text-[#13231E] rounded ${FOCO}`}>
          Datos del lead inventado
        </summary>
        <p className="text-xs text-muted-foreground mt-1">
          Lo que el bot sabría del lead por el formulario. Lo que se deje vacío le llega como «no indicado».
        </p>
        <div className="grid gap-2 sm:grid-cols-2 mt-2">
          {CAMPOS_LEAD.map(([campo, nombre]) => (
            <div key={campo} className="space-y-0.5 min-w-0">
              <label htmlFor={`bot-sim-${campo}`} className="text-xs text-muted-foreground">{nombre}</label>
              <Input id={`bot-sim-${campo}`} value={lead[campo]} onChange={(e) => ponerLead(campo, e.target.value)}
                autoComplete="off" className="h-8" data-testid={`bot-sim-${campo}`} />
            </div>
          ))}
          <div className="space-y-0.5 sm:col-span-2">
            <label htmlFor="bot-sim-mensaje" className="text-xs text-muted-foreground">Mensaje libre del formulario</label>
            <Textarea id="bot-sim-mensaje" value={lead.mensaje} onChange={(e) => ponerLead('mensaje', e.target.value)}
              rows={2} className="min-h-[44px]" data-testid="bot-sim-mensaje" />
          </div>
        </div>
        <label className="mt-2 flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={lead.apertura_enviada}
            onChange={(e) => alternarApertura(e.target.checked)} data-testid="bot-sim-apertura" />
          <span>
            Ya se le mandó la plantilla de apertura
            <span className="block text-xs text-muted-foreground">
              Si no, la conversación la empieza el contacto escribiendo por WhatsApp.
            </span>
          </span>
        </label>
        {lead.apertura_enviada ? (
          <div className="mt-2 space-y-1">
            <div className="space-y-0.5 max-w-xs">
              <label htmlFor="bot-sim-via" className="text-xs text-muted-foreground">Por dónde llegó (para la apertura)</label>
              <select id="bot-sim-via" value={via} onChange={(e) => setVia(e.target.value)} data-testid="bot-sim-via"
                className={`flex h-8 w-full rounded-md border border-input bg-background px-2 text-sm ${FOCO}`}>
                <option value="">Sin elegir</option>
                {PROCEDENCIAS.map(([k, nombre]) => (
                  <option key={k} value={k}>{nombre}{procedencias[k] ? '' : ' (sin texto)'}</option>
                ))}
              </select>
            </div>
            <label htmlFor="bot-sim-apertura-texto" className="text-xs text-muted-foreground block">
              Primer mensaje del bot: la apertura de Plantillas tal como le llegaría (con nombre o sin él). Se puede
              cambiar a mano; las {'{{n}}'} que queden, por lo que vería el contacto.
            </label>
            <Textarea id="bot-sim-apertura-texto" value={apertura} onChange={(e) => setAperturaManual(e.target.value)} rows={3}
              data-testid="bot-sim-apertura-texto" />
            {aperturaManual !== null ? (
              <Button type="button" variant="link" size="sm" className="h-auto px-0"
                onClick={() => setAperturaManual(null)} data-testid="bot-sim-apertura-recargar">
                Volver a poner la apertura de Plantillas
              </Button>
            ) : null}
            {!automatica.texto ? (
              <p className="text-xs text-amber-800">No hay apertura guardada en Plantillas: escríbela aquí.</p>
            ) : null}
          </div>
        ) : null}
      </details>

      <div className="rounded-lg border overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b bg-background">
          <label className={`flex items-center gap-2 text-xs ${hayCambiosEditor ? '' : 'text-muted-foreground'}`}>
            <input type="checkbox" checked={conEditor} disabled={!hayCambiosEditor}
              onChange={(e) => setUsarEditor(e.target.checked)} data-testid="bot-sim-usar-editor" />
            Probar con el texto del editor, sin guardarlo
            {!hayCambiosEditor ? <span>(no hay cambios sin guardar)</span> : null}
          </label>
          <Button type="button" variant="outline" size="sm" onClick={empezarDeNuevo}
            disabled={!empezada && !error} data-testid="bot-sim-reiniciar">
            <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" /> Empezar de nuevo
          </Button>
        </div>
        <div ref={chatRef} className="h-[26rem] overflow-y-auto px-3 py-2 bg-[#EFEAE2] dark:bg-[#0B141A]"
          data-testid="bot-sim-chat" aria-live="polite">
          {lead.apertura_enviada && apertura.trim() ? (
            <>
              <Burbuja mia testid="bot-sim-apertura-burbuja"
                etiqueta={<><Bot className="w-3 h-3" aria-hidden="true" /> Plantilla de apertura</>}>
                {apertura.trim()}
              </Burbuja>
              {!empezada && botones.length ? (
                <div className="flex justify-end flex-wrap gap-1.5 mt-1">
                  {botones.map((b) => (
                    <button key={b} type="button" onClick={() => enviar(b)} disabled={pensando || sinClave}
                      className={`rounded-lg bg-white px-3 py-1 text-sm text-[#027EB5] shadow-sm hover:bg-sky-50 disabled:opacity-50 ${FOCO}`}
                      data-testid="bot-sim-boton">
                      {b}
                    </button>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
          {!empezada && !(lead.apertura_enviada && apertura.trim()) ? (
            <p className="text-center text-xs text-muted-foreground py-8" data-testid="bot-sim-vacio">
              Escribe abajo como si fueras el contacto. No se envía nada a nadie.
            </p>
          ) : null}
          {turnos.map((t) => (t.autor === 'contacto' ? (
            <Burbuja key={t.id} testid="bot-sim-contacto">{t.texto}</Burbuja>
          ) : (
            <div key={t.id} data-testid="bot-sim-respuesta">
              {t.mensajes.length ? t.mensajes.map((m, i) => (
                <Burbuja key={i} mia testid="bot-sim-bot"
                  etiqueta={i === 0 ? <><Bot className="w-3 h-3" aria-hidden="true" /> Bot{t.conEditor ? ' · texto del editor' : ''}</> : null}>
                  {m}
                </Burbuja>
              )) : (
                <p className="text-right text-xs text-muted-foreground mt-1.5">El bot no ha escrito nada en esta respuesta.</p>
              )}
              <div className="flex justify-end mt-1">
                <div className="max-w-[88%] sm:max-w-[75%] rounded-md bg-white/80 dark:bg-[#111B21]/80 px-2 py-1.5">
                  <EstadoRespuesta estado={t.estado} ms={t.ms} uso={t.uso} modelo={t.modelo} />
                </div>
              </div>
            </div>
          )))}
          {pensando ? (
            <div className="flex justify-end mt-1.5" data-testid="bot-sim-pensando">
              <span className="inline-flex items-center gap-1.5 rounded-lg bg-white/80 px-2.5 py-1 text-xs text-[#54656F]">
                <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> El bot está pensando…
              </span>
            </div>
          ) : null}
          {error ? (
            <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs text-red-800 flex flex-wrap items-center gap-2"
              data-testid="bot-sim-error" role="alert">
              <span className="flex-1 min-w-0">{error}</span>
              {empezada ? (
                <Button type="button" variant="outline" size="sm" onClick={reintentar} data-testid="bot-sim-reintentar">
                  <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" /> Reintentar
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="border-t p-2 bg-background space-y-1.5">
          <div className="flex items-end gap-2">
            <Textarea value={entrada} onChange={(e) => setEntrada(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
              placeholder="Escribe como el contacto…" aria-label="Mensaje del contacto inventado"
              title="Enter manda; Mayúsculas+Enter salta de línea" disabled={sinClave}
              className="min-h-[42px] max-h-32 resize-none" data-testid="bot-sim-entrada" />
            <Button type="button" onClick={() => enviar()} disabled={pensando || sinClave || !entrada.trim()}
              aria-label="Mandar como el contacto" className={`h-[42px] ${BOTON_VERDE}`} data-testid="bot-sim-enviar">
              {pensando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <span>Como si mandara:</span>
            <Button type="button" variant="outline" size="sm" className="h-7" onClick={() => enviar('[foto]')}
              disabled={pensando || sinClave} data-testid="bot-sim-foto">
              <Camera className="w-3.5 h-3.5" aria-hidden="true" /> una foto
            </Button>
            <Button type="button" variant="outline" size="sm" className="h-7" onClick={() => enviar('[audio]')}
              disabled={pensando || sinClave} data-testid="bot-sim-audio">
              <Mic className="w-3.5 h-3.5" aria-hidden="true" /> un audio sin transcribir
            </Button>
          </div>
          {sinClave ? (
            <p className="text-xs text-amber-800" data-testid="bot-sim-sin-clave">Para probar hace falta la clave de la API de Claude.</p>
          ) : sinInstrucciones ? (
            <p className="text-xs text-amber-800">No hay instrucciones guardadas: guárdalas o marca «Probar con el texto del editor».</p>
          ) : (
            <p className="text-xs text-muted-foreground">Cada respuesta es una llamada de verdad a Claude (cuenta en el uso), pero no se envía nada.</p>
          )}
        </div>
      </div>
    </div>
  );
}
